import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import CTASection from "@/components/site/CTASection";
import { usePageMeta } from "@/components/site/SEO";
import { ASSETS } from "@/content/assets";

const INTRO_PARAGRAPHS = [
  "We work with schools, students, families, organisations and curious people of all kinds to create experiences that are thoughtful, immersive and rooted in the places we visit. Our journeys bring together educators, explorers, scientists, artists, storytellers and local experts — people who know how to make a place come alive.",
  "We don't believe every journey needs to have a lesson at the end. But we do believe that when you pay attention, ask better questions and experience something for yourself, you're likely to leave seeing the world a little differently.",
];

const BELIEFS = [
  {
    title: "Curiosity comes first.",
    text: "A good question can take you further than a good answer. We create experiences that leave room to wonder, investigate and figure things out.",
  },
  {
    title: "People make places.",
    text: "Every landscape has stories, histories and people connected to it. We believe in learning with — not simply about — the communities we encounter.",
  },
  {
    title: "There is always more than one way to see things.",
    text: "A forest can be a habitat, a scientist's field site, a community's livelihood, a source of stories or simply a beautiful place to sit. We like looking at things from more than one angle.",
  },
  {
    title: "Planned, but not scripted.",
    text: "Every journey is carefully planned, while leaving enough flexibility to respond to what the place and the people bring. A sudden change in weather, an unexpected sighting or a conversation that takes us somewhere interesting can become part of the experience.",
  },
];

export default function About() {
  usePageMeta(
    "About Us",
    "Founded by educators and explorers, Wandering Glider Experiences creates place-based learning journeys that treat the world as a living classroom.",
  );

  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Wander with purpose"
        intro="Wandering Glider Experiences creates journeys that bring people into closer contact with the world — its landscapes, stories, people, creatures and the questions they raise. Sometimes that means walking through a forest, following an old trail, meeting someone who knows a place intimately, or simply slowing down enough to notice what is usually missed."
        image={ASSETS.aboutImage}
      />

      <section className="container-x pb-20 sm:pb-28" data-testid="about-story">
        <div className="max-w-3xl">
          <div>
            <Reveal>
              <div className="space-y-6">
                {INTRO_PARAGRAPHS.map((paragraph, i) => (
                  <p key={i} className="text-lg leading-relaxed text-ink/85">
                    {paragraph}
                  </p>
                ))}
              </div>
            </Reveal>
            <Reveal>
              <h2 className="mt-14 text-2xl font-medium tracking-tight sm:text-3xl">We believe</h2>
            </Reveal>
            <div className="mt-6 grid gap-x-14 sm:grid-cols-2">
              {BELIEFS.map((belief, i) => (
                <Reveal key={belief.title} delay={i * 0.05}>
                  <div className="py-7">
                    <h3 className="font-serif text-xl font-medium tracking-tight">{belief.title}</h3>
                    <p className="mt-3 text-base leading-relaxed text-ink/75">{belief.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        heading="Curious about how a journey comes together?"
        primary={{ label: "Explore Our Approach", to: "/our-approach", testId: "about-approach-cta" }}
        secondary={{ label: "Meet Our People", to: "/our-people", testId: "about-people-cta" }}
        testId="about-cta"
      />
    </>
  );
}
